import heic2any from "heic2any"

// Client-side photo compression that mirrors the old server (Lambda) behavior:
//   - decode the image (transcoding HEIC/HEIF to JPEG first, since browsers
//     can't decode HEIC and the face-index step requires JPEG/PNG),
//   - bake EXIF orientation into the pixels,
//   - downscale to HALF width & height,
//   - encode PNG when the original was png, otherwise JPEG at quality ~0.2
//     (matches the old Lambda's quality=20). JPEG encoding flattens alpha to RGB.

export interface CompressedImage {
  blob: Blob
  mime: string
}

export type CompressionResult =
  | { status: "ok"; image: CompressedImage }
  | { status: "skipped"; reason: string }

// Canvas has per-browser size/area limits. Beyond these the draw/encode step
// silently fails, so we bail out and let the server-side fallback handle it.
const MAX_CANVAS_DIMENSION = 16384
const MAX_CANVAS_AREA = 16384 * 16384
const JPEG_QUALITY = 0.2

export const isHeicFile = (file: File, ext: string) =>
  ext === "heic" ||
  ext === "heif" ||
  file.type === "image/heic" ||
  file.type === "image/heif"

// Transcode a HEIC/HEIF blob to a JPEG blob. Used both to normalize the stored
// original (so it's web-viewable on every platform) and, defensively, inside
// compressImage. quality is kept high here since the derivatives recompress hard.
export async function transcodeHeicToJpeg(
  file: File,
  quality = 0.92
): Promise<Blob> {
  const converted = await heic2any({ blob: file, toType: "image/jpeg", quality })
  return Array.isArray(converted) ? converted[0] : converted
}

// `imageOrientation: "from-image"` bakes EXIF rotation into the decoded pixels.
async function decodeToBitmap(blob: Blob): Promise<ImageBitmap> {
  try {
    // Cast: TS 4.9's lib.dom omits "from-image" from ImageOrientation, but all
    // targeted browsers support it.
    return await createImageBitmap(blob, {
      imageOrientation: "from-image",
    } as unknown as ImageBitmapOptions)
  } catch {
    // Older engines may not support the option; fall back to a plain decode.
    return await createImageBitmap(blob)
  }
}

export async function compressImage(
  file: File,
  ext: string
): Promise<CompressionResult> {
  try {
    // Normally the pipeline transcodes HEIC to JPEG before we get here; this
    // branch is a defensive fallback if a HEIC blob reaches compression.
    let source: Blob = file
    if (isHeicFile(file, ext)) {
      source = await transcodeHeicToJpeg(file)
    }

    const bitmap = await decodeToBitmap(source)
    const targetW = Math.max(1, Math.floor(bitmap.width / 2))
    const targetH = Math.max(1, Math.floor(bitmap.height / 2))

    if (
      targetW > MAX_CANVAS_DIMENSION ||
      targetH > MAX_CANVAS_DIMENSION ||
      targetW * targetH > MAX_CANVAS_AREA
    ) {
      bitmap.close?.()
      return { status: "skipped", reason: "Image too large for client compression" }
    }

    const canvas = document.createElement("canvas")
    canvas.width = targetW
    canvas.height = targetH
    const ctx = canvas.getContext("2d")
    if (!ctx) {
      bitmap.close?.()
      return { status: "skipped", reason: "Canvas 2D context unavailable" }
    }
    ctx.drawImage(bitmap, 0, 0, targetW, targetH)
    bitmap.close?.()

    // PNG keeps its format; anything else encodes to JPEG. Encoding to JPEG
    // drops the alpha channel, flattening the image to RGB.
    const mime = ext === "png" ? "image/png" : "image/jpeg"
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(
        resolve,
        mime,
        mime === "image/jpeg" ? JPEG_QUALITY : undefined
      )
    )
    if (!blob) {
      return { status: "skipped", reason: "Canvas encoding failed" }
    }
    return { status: "ok", image: { blob, mime } }
  } catch (err: any) {
    // TODO: very large images (canvas limits) and HEIC decode failures land
    // here. These must NOT go through the direct-to-R2 flow — a server-side
    // compression fallback path should handle them once it exists.
    return { status: "skipped", reason: err?.message || "Compression failed" }
  }
}
